import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    Divider,
    Grid,
    Card,
    CardMedia,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    Tabs,
    Tab,
    Autocomplete,
    InputAdornment,
} from '@mui/material';

import {
    Home,
    Business,
    Close as CloseIcon,
    ShoppingCart as ShoppingCartIcon,
    ShoppingBagOutlined,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Remove as RemoveIcon,
    Add as AddIconSmall,
    ShoppingCartOutlined as ShoppingCartOutlinedIcon,
    LocationOn,
    CheckCircle,
    Search as SearchIcon,
} from '@mui/icons-material';

import { shopModel } from '../Axios/AxiosConfig';

/* ========= Types ========= */

interface CartItem {
    id: string;
    sku: string;
    productName: string;
    quantity: number;
    price: number;
    src: string;
    desc: string;
    actualQty: string;
    company?: string;
    companyid: string;
}

interface Address {
    id: string;
    address_type: string;
    Email: string;
    Name: string;
    Phone: string;
    address_details: string;
    City: string;
    State: string;
    Country: string;
    Pincode: string;
}

interface Coupon {
    id: string;
    couponName: string;
    couponType: 'fixedAmount' | 'percentage';
    discount?: number;
    cashRedeem?: number;
    minValue?: number;
    maxUsage?: number;
    applicableTo: string[];
    valid: boolean;
    expiration?: string;
}

interface CartResponse {
    items: CartItem[];
    totalItems: number;
    totalPrice: number;
    deliverycharges: number;
    userId: string;
}

interface ShippingAddress {
    Name?: string;
    address_details?: string;
    City?: string;
    State?: string;
    Pincode?: string;
    Country?: string;
}

interface ShopOrderItem {
    id?: string;
    sku?: string;
    name?: string;
    image?: string;
    qty: number;
    actualprice: number;
    price: number;
    discount?: number;
    actualqty?: string;
    company?: string;
}

interface ShopOrderHistoryStatus {
    statusId: string;
    status: string;
    timestamp: string;
}

interface ShopOrderData {
    paymentmode: string;
    Orderid: string;
    cartitems: ShopOrderItem[];
    OrderStatus: string;
    OrderHistoryStatus: ShopOrderHistoryStatus[];
    companyid: string;
    userid: string;
    dateandtime: string;
    email?: string;
    phone?: string;
    address: string;
    delivery_charges: string;
    subTotal: number;
    total: number;
    redeemcredits?: number;
    creditsDiscount?: number;
    couponDiscount?: number;
    customerDetails: any; // or a tighter type if you like
}

interface ShopOrder {
    id: string;
    UID: string;
    Data: ShopOrderData;
}

const SupplementOrdersPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [cartData, setCartData] = useState<CartResponse>({
        items: [],
        totalItems: 0,
        totalPrice: 0,
        deliverycharges: 0,
        userId: '',
    });

    const [userInfo, setUserInfo] = useState<{ name?: string; email?: string }>({});

    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState<Coupon | null>(null);
    const [couponError, setCouponError] = useState('');
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);

    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [transactionId, setTransactionId] = useState('');
    const [intiateOrderResponse, setIntiateOrderResponse] = useState<any>(null);
    const [isLoadingOrders, setIsLoadingOrders] = useState(false);
    const [activeTab, setActiveTab] = useState(0); // 0 = Cart, 1 = Orders
    const [orders, setOrders] = useState<ShopOrder[]>([]);
    const [selectedOrder, setSelectedOrder] = useState<ShopOrder | null>(null);
    const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);

    // Add Items Dialog State
    const [isAddItemsDialogOpen, setIsAddItemsDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [testKits, setTestKits] = useState<any[]>([]);
    const [isTestKitsLoading, setIsTestKitsLoading] = useState(false);
    const [isAddingItems, setIsAddingItems] = useState(false);

    const [address, setAddress] = useState<Address | null>(null);
    const [isLoadingAddress, setIsLoadingAddress] = useState<boolean>(false);
    const [addressError, setAddressError] = useState<string | null>(null);
    const [userAddresses, setUserAddresses] = useState<Address[]>([]);

    const [dialogState, setDialogState] = useState({
        open: false,
        editingAddress: null as Address | null,
        isEditing: false,
        formData: {
            address_type: 'home',
            custom_address_type: '',
            Name: '',
            Phone: '',
            Email: '',
            address_details: '',
            City: '',
            State: '',
            Country: 'India',
            Pincode: '',
        },
    });


    /* ========= Helpers ========= */

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleAddressTypeChange = (type: 'home' | 'office' | 'others') => {
        setDialogState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                address_type: type,
                // reset custom type if switching away from "others"
                custom_address_type: type === 'others' ? prev.formData.custom_address_type : '',
            },
        }));
    };

    const handleInputChange = (
        e:
            | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            | { target: { name: string; value: any } }
    ) => {
        const { name, value } = e.target;
        setDialogState(prev => ({
            ...prev,
            formData: {
                ...prev.formData,
                [name]: value,
            },
        }));
    };

    /* ========= Data Fetching ========= */

    useEffect(() => {
        const state = location.state as { userId: string } | null;
        if (state?.userId) {
            setCartData(prev => ({ ...prev, userId: state.userId }));

            fetchCart(state.userId);
            fetchUserAddress(state.userId);
        } else {
            setError('No user ID provided');
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);



    const fetchCart = async (id: string) => {
        try {
            setLoading(true);
            const response = await shopModel.get<CartResponse>(
                `/cart/${id}`
            );
            if (response.status === 200 && response.data) {
                setCartData(response.data);
            } else if (response.status === 404) {
                setCartData(prev => ({ ...prev, items: [], totalItems: 0, totalPrice: 0 }));
            }
        } catch (err: any) {
            if (err?.response?.status === 404) {
                setCartData(prev => ({ ...prev, items: [], totalItems: 0, totalPrice: 0 }));
            } else {
                console.error('Error fetching cart items:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchUserAddress = async (userId: string) => {
        setIsLoadingAddress(true);
        setAddressError(null);
        try {
            const response = await shopModel.get(
                `/getAddress/${userId}`
            );
            if (response.data) {
                setAddress(response.data.currentAddress || null);
                setUserAddresses(response.data.addresses || []);
            }
        } catch (err) {
            console.error('Error fetching address:', err);
            setAddressError('Failed to load address');
        } finally {
            setIsLoadingAddress(false);
        }
    };

    const fetchSearchSuggestions = async (query: string) => {
        if (!query.trim()) {
            setSearchSuggestions([]);
            return;
        }

        try {
            setIsSearching(true);
            const response = await shopModel.post('/SearchProducts', { search: query.trim() });
            const data: any[] = Array.isArray(response?.data) ? response.data : [];
            setSearchSuggestions(data);
        } catch (err) {
            console.error('Error fetching search suggestions:', err);
            setSearchSuggestions([]);
        } finally {
            setIsSearching(false);
        }
    };

    const fetchTestKits = async () => {
        try {
            setIsTestKitsLoading(true);
            const response = await shopModel.get('/getAllTestkit', {});
            const kits = response.data?.data || response.data || [];
            setTestKits(Array.isArray(kits) ? kits : []);
        } catch (err) {
            console.error('Failed to load test kits', err);
        } finally {
            setIsTestKitsLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchTerm && isAddItemsDialogOpen) {
                fetchSearchSuggestions(searchTerm);
            } else {
                setSearchSuggestions([]);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, isAddItemsDialogOpen]);

    const fetchUserAddresses = async () => {
        try {
            const state = location.state as { userId: string } | null;
            if (!state?.userId) return [];

            const response = await axios.get(
                `${process.env.REACT_APP_SHOP_URL}/getAddress/${state.userId}`
            );

            if (response.data?.addresses) {
                setUserAddresses(response.data.addresses);
                return response.data.addresses;
            }
            return [];
        } catch (err) {
            console.error('Error fetching addresses:', err);
            setAddressError('Failed to load addresses');
            return [];
        }
    };

    const getOrders = async () => {
        const state = location.state as { userId: string } | null;
        if (!state?.userId) return;

        setIsLoadingOrders(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_SHOP_URL}/getOrders`,
                { UID: state.userId }
            );

            if (res.data) {
                setOrders(res.data as ShopOrder[]);
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
        } finally {
            setIsLoadingOrders(false);
        }
    };




    const handleOpenAddAddress = async () => {
        const state = location.state as { userId: string } | null;
        if (state?.userId) {
            await fetchUserAddresses();
        }

        setDialogState({
            open: true,
            editingAddress: null,
            isEditing: false,
            formData: {
                address_type: 'home',
                custom_address_type: '',
                Name: userInfo.name || '',
                Phone: '',
                Email: userInfo.email || '',
                address_details: '',
                City: '',
                State: '',
                Country: 'India',
                Pincode: '',
            },
        });
    };

    const handleOpenEditAddress = (addr: Address) => {
        setDialogState({
            open: true,
            editingAddress: addr,
            isEditing: true,
            formData: {
                address_type:
                    addr.address_type === 'home' || addr.address_type === 'office'
                        ? addr.address_type
                        : 'others',
                custom_address_type:
                    addr.address_type !== 'home' && addr.address_type !== 'office'
                        ? addr.address_type
                        : '',
                Name: addr.Name,
                Phone: addr.Phone,
                Email: addr.Email,
                address_details: addr.address_details,
                City: addr.City,
                State: addr.State,
                Country: addr.Country,
                Pincode: addr.Pincode,
            },
        });
    };

    const handleCloseDialog = () => {
        setDialogState(prev => ({ ...prev, open: false }));
    };

    const handleSubmitAddress = async () => {
        try {
            const state = location.state as { userId: string } | null;
            if (!state?.userId) return;

            const finalType =
                dialogState.formData.address_type === 'others'
                    ? dialogState.formData.custom_address_type
                    : dialogState.formData.address_type;

            const addressData = {
                ...dialogState.formData,
                address_type: finalType,
            };

            if (dialogState.isEditing && dialogState.editingAddress) {
                await axios.put(
                    `${process.env.REACT_APP_SHOP_URL}/editAddress/${state.userId}/${dialogState.editingAddress.id}`,
                    addressData
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_SHOP_URL}/addAddress/${state.userId}`,
                    { ...addressData, id: Date.now().toString() }
                );
            }

            await fetchUserAddress(state.userId);
            handleCloseDialog();
        } catch (err) {
            console.error('Error saving address:', err);
        }
    };

    const handleSetCurrentAddress = async (addr: Address) => {
        try {
            const state = location.state as { userId: string } | null;
            if (!state?.userId) return;

            await axios.put(
                `${process.env.REACT_APP_SHOP_URL}/currentAddress/${state.userId}/${addr.id}`,
                addr
            );
            await fetchUserAddress(state.userId);
            handleCloseDialog();
        } catch (err) {
            console.error('Error setting current address:', err);
        }
    };

    const handleDeleteAddress = async (addressId: string) => {
        try {
            const state = location.state as { userId: string } | null;
            if (!state?.userId) return;

            await axios.delete(
                `${process.env.REACT_APP_SHOP_URL}/deleteAddress/${state.userId}/${addressId}`
            );
            await fetchUserAddress(state.userId);
        } catch (err) {
            console.error('Error deleting address:', err);
        }
    };

    /* ========= Cart Handlers ========= */

    const handleQuantityChange = async (itemId: string, newQuantity: number) => {
        if (newQuantity < 1) return;
        if (!cartData.userId) return;

        try {
            const state = location.state as { userId: string } | null;
            if (!state?.userId) return;
            const response = await shopModel.patch(
                `/cart/${state.userId}/${itemId}`,
                { newQuantity }
            );

            if (response.data) {
                setCartData({
                    items: response.data.items || [],
                    totalItems: response.data.totalItems || 0,
                    totalPrice: response.data.totalPrice || 0,
                    deliverycharges: response.data.deliverycharges || 0,
                    userId: response.data.userId || cartData.userId,
                });
            } else {
                fetchCart(cartData.userId);
            }
        } catch (err) {
            console.error('Error updating quantity:', err);
            setError('Failed to update item quantity');
        }
    };

    const handleRemoveItem = async (itemId: string) => {
        if (!cartData.userId) return;
        try {
            const response = await axios.delete(
                `${process.env.REACT_APP_SHOP_URL}/cart/${cartData.userId}/${itemId}`
            );
            if (response.data) {
                setCartData({
                    items: response.data.items || [],
                    totalItems: response.data.totalItems || 0,
                    totalPrice: response.data.totalPrice || 0,
                    deliverycharges: response.data.deliverycharges || 0,
                    userId: response.data.userId || cartData.userId,
                });
            } else {
                fetchCart(cartData.userId);
            }
        } catch (err) {
            console.error('Error removing item:', err);
            setError('Failed to remove item from cart');
        }
    };

    const handleAddItemToCart = async (product: any) => {
        if (!cartData.userId || !product) return;

        try {
            setIsAddingItems(true);
            const productId = product.id || product._id || product.skuid;
            
            // Per backend code: { userId, item: { id, price, quantity, companyid, ... } }
            const payload = {
                userId: cartData.userId,
                item: {
                    id: productId,
                    sku: product.sku || product.skuid || '',
                    productName: product.name || product.productName || 'Unknown Product',
                    quantity: 1,
                    price: parseFloat(product.discount_price || product.price || 0),
                    src: product.imgurl || product.product_image_url || product.image || product.src || '',
                    desc: product.desc || product.detaildesc || '',
                    actualQty: product.qty || '',
                    company: product.company || '',
                    companyid: product.company_id || product.companyid || ''
                }
            };

            const response = await axios.post(
                `${process.env.REACT_APP_SHOP_URL}/cart`,
                payload
            );

            if (response.data) {
                // Backend returns updated cart
                setCartData(response.data);
            } else {
                await fetchCart(cartData.userId);
            }
        } catch (err) {
            console.error('Error adding item to cart:', err);
            alert('Failed to add item to cart');
        } finally {
            setIsAddingItems(false);
        }
    };

    /* ========= Coupon / Totals ========= */

    const handleApplyCoupon = async () => {
        const state = location.state as { userId: string } | null;
        if (!couponCode.trim()) {
            setCouponError('Please enter a coupon code');
            return;
        }

        setIsApplyingCoupon(true);
        setCouponError('');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SHOP_URL}/AvailCoupon`,
                {
                    uid: state?.userId,
                    coupon: couponCode,
                    applicableTo: 'Shop',
                    total: cartData.totalPrice,
                }
            );

            const coupon: Coupon = response.data;

            if (!coupon.valid) {
                throw new Error('This coupon is no longer valid');
            }

            if (coupon.expiration && new Date(coupon.expiration) < new Date()) {
                throw new Error('This coupon has expired');
            }

            if (coupon.minValue && cartData.totalPrice < coupon.minValue) {
                throw new Error(`Minimum order value of ₹${coupon.minValue} required`);
            }

            setAppliedCoupon(coupon);
        } catch (err: any) {
            setCouponError(
                err.response?.data?.error ||
                err.message ||
                'Failed to apply coupon'
            );
            setAppliedCoupon(null);
        } finally {
            setIsApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        setCouponError('');
    };

    const calculateDiscount = () => {
        if (!appliedCoupon) return 0;

        if (
            appliedCoupon.couponType === 'fixedAmount' &&
            appliedCoupon.cashRedeem
        ) {
            return Math.min(appliedCoupon.cashRedeem, cartData.totalPrice);
        }

        if (
            appliedCoupon.couponType === 'percentage' &&
            appliedCoupon.discount
        ) {
            return (cartData.totalPrice * appliedCoupon.discount) / 100;
        }

        return 0;
    };

    const calculateTotal = () => {
        const discount = calculateDiscount();
        return Math.max(
            0,
            cartData.totalPrice - discount + cartData.deliverycharges
        );
    };

    /* ========= Checkout ========= */

    const handleCheckout = async () => {
        if (!address) {
            alert('Please select a delivery address');
            return;
        }
        if (!cartData.userId) return;

        setIsCheckoutLoading(true);
        try {
            const txId = `tx_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            setTransactionId(txId);

            const initResponse = await axios.post(
                `${process.env.REACT_APP_SHOP_URL}/intiateOrderProcess`,
                {
                    userid: cartData.userId,
                    Cart: cartData.items,
                    Address: { ...address },
                    redeemcredits: 0,
                    transactionId: txId,
                    couponId: appliedCoupon?.id || '',
                }
            );

            if (initResponse.status === 200) {
                setIntiateOrderResponse(initResponse.data);
                setShowConfirmation(true);
            } else {
                throw new Error('Failed to initiate order');
            }
        } catch (err) {
            console.error('Error preparing checkout:', err);
            alert('Failed to prepare checkout. Please try again.');
        } finally {
            setIsCheckoutLoading(false);
        }
    };

    const confirmOrder = async () => {
        if (!cartData.userId || !intiateOrderResponse) return;

        setIsCheckoutLoading(true);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SHOP_URL}/CreateAndUpdateOrder`,
                {
                    userid: cartData.userId,
                    Orders: intiateOrderResponse.results,
                }
            );

            if (response.data && response.data.success) {

                await fetchCart(cartData.userId);
                setShowConfirmation(false);
                setActiveTab(1);
                getOrders();
                setAppliedCoupon(null);
            } else {
                throw new Error(
                    response.data?.message || 'Failed to finalize order'
                );
            }
        } catch (err: any) {
            console.error('Order error:', err);
            alert(
                err.response?.data?.message ||
                'Failed to place order. Please try again.'
            );
        } finally {
            setIsCheckoutLoading(false);
        }
    };

    /* ========= Tabs ========= */

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (newValue === 1) getOrders();
    };


    /* ========= Loading / Error ========= */

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="60vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3, pt: 1.5 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    /* ========= Render ========= */

    return (
        <Box sx={{ p: 2, pt: 1.5 }}>
            <Box mb={3}>
                {/* Tabs */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 0.5,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid',
                        borderColor: 'divider',
                        width: 'fit-content',
                        mb: 2,
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        sx={{
                            minHeight: 'auto',
                            '& .MuiTabs-flexContainer': {
                                gap: 1,
                            },
                        }}
                        TabIndicatorProps={{ style: { display: 'none' } }}
                    >
                        <Tab
                            label={
                                <Box
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        borderRadius: 1.5,
                                        fontWeight: activeTab === 0 ? 600 : 400,
                                        color:
                                            activeTab === 0
                                                ? 'primary.main'
                                                : 'text.secondary',
                                        bgcolor:
                                            activeTab === 0
                                                ? 'primary.light'
                                                : 'transparent',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor:
                                                activeTab === 0
                                                    ? 'primary.light'
                                                    : 'action.hover',
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <ShoppingCartIcon fontSize="small" />
                                    Cart
                                    {cartData.items.length > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                fontSize: 11,
                                                px: 0.75,
                                                py: 0.15,
                                                borderRadius: 10,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {cartData.items.length}
                                        </Box>
                                    )}
                                </Box>
                            }
                            sx={{ minHeight: 'auto', p: 0 }}
                        />
                        <Tab
                            label={
                                <Box
                                    sx={{
                                        px: 3,
                                        py: 1,
                                        borderRadius: 1.5,
                                        fontWeight: activeTab === 1 ? 600 : 400,
                                        color:
                                            activeTab === 1
                                                ? 'primary.main'
                                                : 'text.secondary',
                                        bgcolor:
                                            activeTab === 1
                                                ? 'primary.light'
                                                : 'transparent',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            bgcolor:
                                                activeTab === 1
                                                    ? 'primary.light'
                                                    : 'action.hover',
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}
                                >
                                    <ShoppingBagOutlined fontSize="small" />
                                    Orders
                                    {orders.length > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 0.5,
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                fontSize: 11,
                                                px: 0.75,
                                                py: 0.15,
                                                borderRadius: 10,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {orders.length}
                                        </Box>
                                    )}
                                </Box>
                            }
                            sx={{ minHeight: 'auto', p: 0 }}
                        />
                    </Tabs>
                </Paper>

                {/* Subtitle */}
                <Box
                    sx={{
                        ml: 1,
                        mt: 0.5,
                        opacity: 1,
                        height: 'auto',
                        overflow: 'hidden',
                        visibility: 'visible',
                    }}
                >
                    <Typography variant="body2" color="textSecondary">
                        {activeTab === 0
                            ? userInfo.name
                                ? `Items in ${userInfo.name}'s cart`
                                : 'Your shopping cart'
                            : userInfo.name
                                ? `${userInfo.name}'s order history`
                                : 'Your order history'}
                    </Typography>
                </Box>
            </Box>

            {/* ===== Cart Tab ===== */}
            {activeTab === 0 && (
                <Grid container spacing={2}>
                    {/* Address */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                mb: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1.5,
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight={600}>
                                    <LocationOn
                                        color="primary"
                                        sx={{
                                            verticalAlign: 'middle',
                                            mr: 1,
                                            fontSize: '1.2rem',
                                        }}
                                    />
                                    Delivery Address
                                </Typography>
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleOpenAddAddress();
                                    }}
                                    sx={{ textTransform: 'none' }}
                                    startIcon={!address && <AddIcon />}
                                >
                                    {address ? 'Change' : 'Add Address'}
                                </Button>
                            </Box>

                            {isLoadingAddress ? (
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    py={2}
                                >
                                    <CircularProgress size={24} />
                                </Box>
                            ) : addressError ? (
                                <Alert severity="error" sx={{ mb: 1 }}>
                                    {addressError}
                                </Alert>
                            ) : address ? (
                                <Box>
                                    <Typography
                                        variant="body1"
                                        fontWeight={400}
                                        gutterBottom
                                    >
                                        {address.Name}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={400}
                                        gutterBottom
                                    >
                                        {address.Phone} | {address.Email}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        fontWeight={500}
                                        gutterBottom
                                    >
                                        {address.address_details}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {address.City}, {address.State} -{' '}
                                        {address.Pincode}
                                    </Typography>
                                </Box>
                            ) : (
                                <Box textAlign="center" py={2}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        No address found
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={handleOpenAddAddress}
                                        startIcon={<AddIcon />}
                                        sx={{ mt: 1 }}
                                    >
                                        Add Delivery Address
                                    </Button>
                                </Box>
                            )}
                        </Paper>
                    </Grid>

                    {/* Cart Items Title & Add Button */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>
                                Cart Items
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                size="small"
                                onClick={() => {
                                    setIsAddItemsDialogOpen(true);
                                    fetchTestKits();
                                }}
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                Add Items
                            </Button>
                        </Box>
                    </Grid>

                    {/* Cart Items List */}
                    <Grid item xs={12} md={8}>
                        {cartData.items.length > 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                }}
                            >
                                {cartData.items.map(item => (
                                    <Card
                                        key={item.id}
                                        sx={{
                                            display: 'flex',
                                            mb: 1.5,
                                            boxShadow: 1,
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            sx={{
                                                width: 100,
                                                height: 100,
                                                objectFit: 'contain',
                                                p: 1,
                                                bgcolor: '#f5f5f5',
                                            }}
                                            image={
                                                item.src ||
                                                'https://via.placeholder.com/150'
                                            }
                                            alt={item.productName}
                                        />
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                flexGrow: 1,
                                                p: 1.5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 500,
                                                            lineHeight: 1.2,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        {item.productName}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            display: 'block',
                                                            mb: 0.5,
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {item.actualQty} • {item.company}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontSize: '0.75rem',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {item.desc.length > 50
                                                            ? `${item.desc.substring(
                                                                0,
                                                                50
                                                            )}...`
                                                            : item.desc}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="primary"
                                                        sx={{
                                                            fontWeight: 600,
                                                            mb: 1,
                                                        }}
                                                    >
                                                        ₹
                                                        {(
                                                            item.price * item.quantity
                                                        ).toFixed(2)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            textDecoration: 'line-through',
                                                            display: 'block',
                                                        }}
                                                    >
                                                        ₹{(item.price * 1.1).toFixed(2)}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="success.main"
                                                        sx={{ display: 'block' }}
                                                    >
                                                        10% off
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mt: 1,
                                                    pt: 1,
                                                    borderTop:
                                                        '1px solid rgba(0, 0, 0, 0.08)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleQuantityChange(
                                                                item.id,
                                                                item.quantity - 1
                                                            )
                                                        }
                                                        disabled={item.quantity <= 1}
                                                        sx={{
                                                            p: 0.5,
                                                            '&:hover': {
                                                                bgcolor: 'action.hover',
                                                            },
                                                        }}
                                                    >
                                                        <RemoveIcon fontSize="small" />
                                                    </IconButton>
                                                    <TextField
                                                        size="small"
                                                        value={item.quantity}
                                                        type="number"
                                                        variant="outlined"
                                                        InputProps={{
                                                            inputProps: {
                                                                min: 1,
                                                                style: {
                                                                    textAlign: 'center',
                                                                    width: '36px',
                                                                    height: '28px',
                                                                    padding: '4px 0',
                                                                    WebkitAppearance: 'none',
                                                                    margin: 0,
                                                                },
                                                            },
                                                        }}
                                                        sx={{
                                                            width: '50px',
                                                            '& .MuiOutlinedInput-root': {
                                                                '& input': {
                                                                    padding: '6px 0',
                                                                    textAlign: 'center',
                                                                },
                                                            },
                                                        }}
                                                        onChange={e =>
                                                            handleQuantityChange(
                                                                item.id,
                                                                parseInt(
                                                                    e.target.value || '1',
                                                                    10
                                                                ) || 1
                                                            )
                                                        }
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleQuantityChange(
                                                                item.id,
                                                                item.quantity + 1
                                                            )
                                                        }
                                                        sx={{
                                                            p: 0.5,
                                                            '&:hover': {
                                                                bgcolor: 'action.hover',
                                                            },
                                                        }}
                                                    >
                                                        <AddIconSmall fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        handleRemoveItem(item.id)
                                                    }
                                                    sx={{
                                                        '&:hover': {
                                                            bgcolor: 'error.light',
                                                            color: 'error.contrastText',
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    </Card>
                                ))}
                            </Box>
                        ) : (
                            <Paper
                                sx={{
                                    p: 3,
                                    textAlign: 'center',
                                    boxShadow: 1,
                                }}
                            >
                                <Box
                                    sx={{ maxWidth: 300, mx: 'auto' }}
                                >
                                    <ShoppingCartOutlinedIcon
                                        sx={{
                                            fontSize: 48,
                                            color: 'text.disabled',
                                            mb: 1,
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle1"
                                        color="text.secondary"
                                        gutterBottom
                                    >
                                        Your cart is empty
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        {userInfo.name
                                            ? `${userInfo.name} hasn't added anything to their cart yet`
                                            : 'The cart is currently empty'}
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        onClick={() => navigate(-1)}
                                        sx={{ mt: 2 }}
                                    >
                                        Go Back
                                    </Button>
                                </Box>
                            </Paper>
                        )}
                    </Grid>

                    {/* Order Summary */}
                    {cartData.items.length > 0 && (
                        <Grid item xs={12} md={4}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    position: 'sticky',
                                    top: 20,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    gutterBottom
                                >
                                    Order Summary
                                </Typography>

                                {/* Coupon */}
                                <Box sx={{ mb: 2 }}>
                                    {!appliedCoupon ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Enter coupon code"
                                                value={couponCode}
                                                onChange={e =>
                                                    setCouponCode(e.target.value)
                                                }
                                                error={!!couponError}
                                                helperText={couponError}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={handleApplyCoupon}
                                                disabled={isApplyingCoupon}
                                            >
                                                {isApplyingCoupon
                                                    ? 'Applying...'
                                                    : 'Apply'}
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent:
                                                    'space-between',
                                                alignItems: 'center',
                                                bgcolor: 'success.light',
                                                p: 1,
                                                borderRadius: 1,
                                                mb: 1,
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="body2"
                                                    color="success.dark"
                                                >
                                                    Coupon Applied:{' '}
                                                    {appliedCoupon.couponName}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="success.dark"
                                                >
                                                    {appliedCoupon.couponType ===
                                                        'fixedAmount'
                                                        ? `₹${appliedCoupon.cashRedeem || 0
                                                        } off`
                                                        : `${appliedCoupon.discount || 0
                                                        }% off`}
                                                </Typography>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveCoupon}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ my: 1.5 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Subtotal ({cartData.totalItems}{' '}
                                            items)
                                        </Typography>
                                        <Typography variant="body2">
                                            ₹
                                            {cartData.totalPrice.toFixed(
                                                2
                                            )}
                                        </Typography>
                                    </Box>

                                    {appliedCoupon && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                mb: 1,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                Discount
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="success.main"
                                            >
                                                -₹
                                                {calculateDiscount().toFixed(
                                                    2
                                                )}
                                            </Typography>
                                        </Box>
                                    )}

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Delivery Charges
                                        </Typography>
                                        <Typography variant="body2">
                                            ₹
                                            {cartData.deliverycharges.toFixed(
                                                2
                                            )}
                                        </Typography>
                                    </Box>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            mb: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={600}
                                        >
                                            Total Amount
                                        </Typography>
                                        <Box textAlign="right">
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={600}
                                            >
                                                ₹
                                                {calculateTotal().toFixed(
                                                    2
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                incl. of taxes
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        onClick={handleCheckout}
                                        disabled={isCheckoutLoading}
                                        sx={{
                                            mt: 1,
                                            py: 1,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            '&:hover': {
                                                boxShadow:
                                                    '0 2px 10px rgba(25, 118, 210, 0.3)',
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor:
                                                    'action.disabledBackground',
                                                color: 'action.disabled',
                                            },
                                        }}
                                    >
                                        {isCheckoutLoading ? (
                                            <CircularProgress
                                                size={24}
                                                color="inherit"
                                            />
                                        ) : (
                                            'Proceed to Checkout'
                                        )}
                                    </Button>
                                </Box>
                            </Paper>
                        </Grid>
                    )}

                    {/* Address Add/Edit Dialog */}
                    <Dialog
                        open={dialogState.open}
                        onClose={handleCloseDialog}
                        maxWidth="md"
                        fullWidth
                    >
                        <DialogTitle>
                            {dialogState.isEditing ? 'Edit Delivery Address' : 'Add Delivery Address'}
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Address Details
                                    </Typography>
                                    <Box display="flex" gap={1} mb={1}>
                                        <Chip
                                            icon={<Home fontSize="small" />}
                                            label="Home"
                                            size="small"
                                            color={dialogState.formData.address_type === 'home' ? 'primary' : 'default'}
                                            onClick={() => handleAddressTypeChange('home')}
                                        />
                                        <Chip
                                            icon={<Business fontSize="small" />}
                                            label="Office"
                                            size="small"
                                            color={dialogState.formData.address_type === 'office' ? 'primary' : 'default'}
                                            onClick={() => handleAddressTypeChange('office')}
                                        />
                                        <Chip
                                            label="Others"
                                            size="small"
                                            color={dialogState.formData.address_type === 'others' ? 'primary' : 'default'}
                                            onClick={() => handleAddressTypeChange('others')}
                                        />
                                    </Box>
                                    {dialogState.formData.address_type === 'others' && (
                                        <TextField
                                            margin="dense"
                                            label="Custom Address Type"
                                            fullWidth
                                            name="custom_address_type"
                                            value={dialogState.formData.custom_address_type}
                                            onChange={handleInputChange}
                                        />
                                    )}
                                    <TextField
                                        margin="dense"
                                        label="Name"
                                        fullWidth
                                        name="Name"
                                        value={dialogState.formData.Name}
                                        onChange={handleInputChange}
                                    />
                                    <TextField
                                        margin="dense"
                                        label="Phone"
                                        fullWidth
                                        name="Phone"
                                        value={dialogState.formData.Phone}
                                        onChange={handleInputChange}
                                    />
                                    <TextField
                                        margin="dense"
                                        label="Email"
                                        fullWidth
                                        name="Email"
                                        value={dialogState.formData.Email}
                                        onChange={handleInputChange}
                                    />
                                    <TextField
                                        margin="dense"
                                        label="Address"
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        name="address_details"
                                        value={dialogState.formData.address_details}
                                        onChange={handleInputChange}
                                    />
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                margin="dense"
                                                label="City"
                                                fullWidth
                                                name="City"
                                                value={dialogState.formData.City}
                                                onChange={handleInputChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                margin="dense"
                                                label="State"
                                                fullWidth
                                                name="State"
                                                value={dialogState.formData.State}
                                                onChange={handleInputChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                margin="dense"
                                                label="Pincode"
                                                fullWidth
                                                name="Pincode"
                                                value={dialogState.formData.Pincode}
                                                onChange={handleInputChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                margin="dense"
                                                label="Country"
                                                fullWidth
                                                name="Country"
                                                value={dialogState.formData.Country}
                                                onChange={handleInputChange}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Existing Addresses
                                    </Typography>
                                    {userAddresses.length > 0 ? (
                                        <List dense>
                                            {userAddresses.map(addr => (
                                                <ListItem key={addr.id} alignItems="flex-start">
                                                    <ListItemText
                                                        primary={`${addr.Name} • ${addr.Phone}`}
                                                        secondary={
                                                            <>
                                                                <Typography variant="body2">{addr.address_type}</Typography>
                                                                <Typography variant="body2">{addr.address_details}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {addr.City}, {addr.State} - {addr.Pincode}
                                                                </Typography>
                                                            </>
                                                        }
                                                    />
                                                    <ListItemSecondaryAction>
                                                        <Button size="small" onClick={() => handleSetCurrentAddress(addr)}>
                                                            Deliver Here
                                                        </Button>
                                                        <IconButton size="small" onClick={() => handleOpenEditAddress(addr)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteAddress(addr.id)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No saved addresses
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} color="inherit">
                                Cancel
                            </Button>
                            <Button onClick={handleSubmitAddress} variant="contained" color="primary">
                                {dialogState.isEditing ? 'Save Changes' : 'Save Address'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Checkout Confirmation Dialog */}
                    <Dialog
                        open={showConfirmation}
                        onClose={() =>
                            !isCheckoutLoading &&
                            setShowConfirmation(false)
                        }
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogTitle>Confirm Your Order</DialogTitle>
                        <DialogContent>
                            <Typography
                                variant="body1"
                                gutterBottom
                            >
                                Are you sure you want to place this
                                order?
                            </Typography>
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    bgcolor: 'background.default',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    gutterBottom
                                >
                                    Order Summary
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="body2">
                                        Subtotal ({cartData.totalItems}{' '}
                                        items):
                                    </Typography>
                                    <Typography variant="body2">
                                        ₹
                                        {cartData.totalPrice.toFixed(
                                            2
                                        )}
                                    </Typography>
                                </Box>
                                {appliedCoupon && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent:
                                                'space-between',
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="body2">
                                            Discount:
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="success.main"
                                        >
                                            -₹
                                            {calculateDiscount().toFixed(
                                                2
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="body2">
                                        Delivery:
                                    </Typography>
                                    <Typography variant="body2">
                                        ₹
                                        {cartData.deliverycharges.toFixed(
                                            2
                                        )}
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontWeight: 'bold',
                                    }}
                                >
                                    <Typography>Total:</Typography>
                                    <Typography>
                                        ₹
                                        {calculateTotal().toFixed(
                                            2
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions
                            sx={{
                                p: 2,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Button
                                onClick={() =>
                                    setShowConfirmation(false)
                                }
                                disabled={isCheckoutLoading}
                                color="inherit"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={confirmOrder}
                                variant="contained"
                                color="primary"
                                disabled={isCheckoutLoading}
                                startIcon={
                                    isCheckoutLoading ? (
                                        <CircularProgress
                                            size={20}
                                            color="inherit"
                                        />
                                    ) : undefined
                                }
                            >
                                {isCheckoutLoading
                                    ? 'Processing...'
                                    : 'Confirm Order'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Grid>
            )}

            {/* ===== Orders Tab ===== */}
            {/* ===== Orders Tab ===== */}


            {/* ===== Orders Tab ===== */}
            {activeTab === 1 && (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        {isLoadingOrders ? (
                            <Box display="flex" justifyContent="center" p={4}>
                                <CircularProgress />
                            </Box>
                        ) : orders.length > 0 ? (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <List>
                                    {orders.map(order => (
                                        <React.Fragment key={order.id}>
                                            <ListItem
                                                button
                                                onClick={() => {
                                                    setSelectedOrder(order);
                                                    setIsOrderDialogOpen(true);
                                                }}
                                                sx={{
                                                    p: 2,
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: 1,
                                                    },
                                                }}
                                            >
                                                <Grid container spacing={2} alignItems="center">
                                                    {/* Order ID + Date */}
                                                    <Grid item xs={12} sm={4}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            color="textSecondary"
                                                        >
                                                            Order #{order.Data.Orderid}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {formatDate(order.Data.dateandtime)}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Status */}
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sm={4}
                                                        sx={{
                                                            textAlign: { xs: 'left', sm: 'center' },
                                                        }}
                                                    >
                                                        <Chip
                                                            label={order.Data.OrderStatus}
                                                            size="small"
                                                            color={
                                                                order.Data.OrderStatus.toLowerCase() === 'delivered'
                                                                    ? 'success'
                                                                    : order.Data.OrderStatus.toLowerCase() === 'shipped'
                                                                        ? 'info'
                                                                        : order.Data.OrderStatus.toLowerCase() === 'processing'
                                                                            ? 'warning'
                                                                            : 'default'
                                                            }
                                                            sx={{ textTransform: 'capitalize' }}
                                                        />
                                                    </Grid>

                                                    {/* Total + item count */}
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sm={4}
                                                        sx={{
                                                            textAlign: { xs: 'left', sm: 'right' },
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="subtitle1"
                                                            fontWeight={600}
                                                        >
                                                            ₹{order.Data.total.toFixed(2)}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {order.Data.cartitems.length}{' '}
                                                            item{order.Data.cartitems.length !== 1 && 's'}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                }}
                            >
                                <ShoppingBagOutlined
                                    sx={{
                                        fontSize: 48,
                                        color: 'text.secondary',
                                        mb: 2,
                                        opacity: 0.5,
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    gutterBottom
                                >
                                    No Orders Yet
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mb: 3 }}
                                >
                                    You haven't placed any orders yet.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setActiveTab(0)}
                                    startIcon={<ShoppingCartIcon />}
                                >
                                    Continue Shopping
                                </Button>
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            )}


            <Dialog
                open={isOrderDialogOpen}
                onClose={() => setIsOrderDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Order #{selectedOrder?.Data?.Orderid || 'N/A'}
                        </Typography>
                        <IconButton onClick={() => setIsOrderDialogOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {selectedOrder && (
                        <Box>
                            <Grid container spacing={3}>
                                {/* Summary */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Order Date:</strong>{' '}
                                        {formatDate(selectedOrder.Data.dateandtime)}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Status:</strong> {selectedOrder.Data.OrderStatus}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Subtotal:</strong> ₹{selectedOrder.Data.subTotal.toFixed(2)}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Delivery:</strong> ₹
                                        {Number(selectedOrder.Data.delivery_charges || 0).toFixed(2)}
                                    </Typography>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Total:</strong> ₹{selectedOrder.Data.total.toFixed(2)}
                                    </Typography>

                                    <Box mt={3}>
                                        <Typography variant="h6" gutterBottom>
                                            Shipping Address
                                        </Typography>
                                        <Typography variant="body2">
                                            {selectedOrder.Data.address || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Grid>

                                {/* Items */}
                                <Grid item xs={12} md={6}>
                                    <Typography variant="h6" gutterBottom>
                                        Order Items
                                    </Typography>
                                    <List>
                                        {selectedOrder.Data.cartitems.map((item, idx) => (
                                            <ListItem key={item.id || `${selectedOrder.id}-${idx}`}>
                                                <Box display="flex" alignItems="center" width="100%">
                                                    <Box mr={2}>
                                                        <img
                                                            src={item.image || '/placeholder-product.jpg'}
                                                            alt={item.name || 'Product'}
                                                            style={{ width: 60, height: 60, objectFit: 'cover' }}
                                                            onError={e => {
                                                                (e.target as HTMLImageElement).src =
                                                                    '/placeholder-product.jpg';
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box flexGrow={1}>
                                                        <Typography variant="subtitle1">
                                                            {item.name || 'Unnamed Product'}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Qty: {item.qty} × ₹{item.actualprice.toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="subtitle1">
                                                        ₹{(item.qty * item.actualprice).toFixed(2)}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </DialogContent>

            </Dialog>

            {/* Add Items Dialog */}
            <Dialog
                open={isAddItemsDialogOpen}
                onClose={() => setIsAddItemsDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" fontWeight={700}>Add Items to Cart</Typography>
                    <IconButton onClick={() => setIsAddItemsDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Search Section */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: "text.secondary", fontWeight: 600 }}>
                            Search Products
                        </Typography>
                        <Autocomplete
                            fullWidth
                            options={searchSuggestions}
                            getOptionLabel={(option) => option.name || option.productName || 'Unknown Product'}
                            inputValue={searchTerm}
                            onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    setSearchTerm(newInputValue);
                                }
                            }}
                            renderOption={(props, option) => {
                                const imagesArray = Array.isArray(option.images) ? option.images : [];
                                const firstImage = imagesArray.length > 0
                                    ? (typeof imagesArray[0] === 'string' ? imagesArray[0] : (imagesArray[0] as any)?.src)
                                    : undefined;
                                const image = option.imgurl || option.product_image_url || option.image || option.src || firstImage;
                                const price = option.discount_price || option.price;
                                return (
                                    <li {...props} key={option.id || option._id || option.skuid}>
                                        <Box display="flex" alignItems="center" gap={2} width="100%">
                                            {image ? (
                                                <Box component="img" src={image} alt=""
                                                    sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5' }}
                                                />
                                            ) : (
                                                <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary">Img</Typography>
                                                </Box>
                                            )}
                                            <Box flexGrow={1}>
                                                <Typography variant="body2" fontWeight={500}>{option.name || option.productName}</Typography>
                                                {option.company && (
                                                    <Typography variant="caption" color="text.secondary" display="block">{option.company}</Typography>
                                                )}
                                            </Box>
                                            <Typography variant="body2" fontWeight={600} color="primary">₹{price}</Typography>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAddItemToCart(option);
                                                }}
                                                disabled={isAddingItems}
                                            >
                                                Add
                                            </Button>
                                        </Box>
                                    </li>
                                );
                            }}
                            onChange={(event, newValue) => {
                                if (newValue) {
                                    handleAddItemToCart(newValue);
                                    setSearchTerm('');
                                    setSearchSuggestions([]);
                                }
                            }}
                            loading={isSearching}
                            filterOptions={(x) => x}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Start typing product name..."
                                    variant="outlined"
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <React.Fragment>
                                                {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                                                {params.InputProps.endAdornment}
                                            </React.Fragment>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Test Kits Section */}
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
                            Test Kits
                            {isTestKitsLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
                        </Typography>
                        {isTestKitsLoading ? (
                            <Box display="flex" justifyContent="center" py={2}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : testKits.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No test kits found.</Typography>
                        ) : (
                            <Grid container spacing={2}>
                                {testKits.map((kit) => {
                                    const kitId = kit.id || kit._id || kit.skuid;
                                    const kitName = kit.name || kit.productName || 'Test Kit';
                                    const kitPrice = kit.discount_price || kit.price || 0;
                                    const imagesArray = Array.isArray(kit.images) ? kit.images : [];
                                    const firstImg = imagesArray.length > 0
                                        ? (typeof imagesArray[0] === 'string' ? imagesArray[0] : (imagesArray[0] as any)?.src)
                                        : undefined;
                                    const kitImage = kit.imgurl || kit.product_image_url || kit.image || kit.src || firstImg;

                                    return (
                                        <Grid item xs={12} sm={6} key={kitId}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    p: 1.5,
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    bgcolor: 'background.paper',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        bgcolor: '#f5f9ff',
                                                    },
                                                }}
                                            >
                                                {kitImage ? (
                                                    <Box component="img" src={kitImage} alt={kitName}
                                                        sx={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 1, bgcolor: '#f5f5f5', flexShrink: 0 }}
                                                    />
                                                ) : (
                                                    <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                        <Typography variant="caption" color="text.secondary">Kit</Typography>
                                                    </Box>
                                                )}
                                                <Box flexGrow={1} minWidth={0}>
                                                    <Typography variant="body2" fontWeight={500} noWrap>{kitName}</Typography>
                                                    <Typography variant="caption" color="primary.main" fontWeight={600}>₹{kitPrice}</Typography>
                                                </Box>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<AddIconSmall />}
                                                    onClick={() => handleAddItemToCart(kit)}
                                                    disabled={isAddingItems}
                                                    sx={{ flexShrink: 0, textTransform: 'none' }}
                                                >
                                                    Add
                                                </Button>
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setIsAddItemsDialogOpen(false)} variant="outlined">
                        Close
                    </Button>
                    <Button
                        onClick={() => {
                            setIsAddItemsDialogOpen(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        variant="contained"
                    >
                        Review Cart
                    </Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default SupplementOrdersPage;
